import { CheckCircle2, GraduationCap, ShieldCheck, Sparkles, ClipboardList, BookOpen, Award, Route, ChevronDown } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import { GoldButton, OutlineButton } from "@/components/Brand";
import { useEnquiry } from "@/components/EnquiryDialog";

const sectionIcons = {
  learn: BookOpen,
  entry: ShieldCheck,
  structure: ClipboardList,
  units: GraduationCap,
  assessment: Award,
  included: Sparkles,
  progression: Route,
};

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-white/70 leading-relaxed">
    <CheckCircle2 size={16} strokeWidth={1.4} className="text-gold shrink-0 mt-1" />
    <span>{children}</span>
  </li>
);

const Block = ({ icon, title, children }) => {
  const Icon = icon;
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Icon size={18} strokeWidth={1.3} className="text-gold" />
        <h4 className="text-xs tracking-[0.2em] uppercase text-gold">{title}</h4>
      </div>
      {children}
    </div>
  );
};

export default function QualifiPage({ overline, titleLines, intro, qualifications, ctaLabel = "Enquire About This Course" }) {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <section className="relative pt-40 md:pt-52 pb-16 md:pb-20 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Overline className="mb-5 block">{overline}</Overline>
          <MaskedLines
            className="font-playfair uppercase leading-[0.95] tracking-tight"
            lineClassName="text-5xl md:text-7xl"
            lines={titleLines}
          />
          <div className="hairline w-40 my-8" />
          <p className="text-white/65 text-lg leading-relaxed max-w-3xl">{intro}</p>
        </div>
      </section>

      <section className="pb-28 bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Accordion type="single" collapsible defaultValue={qualifications[0].id} className="space-y-5">
            {qualifications.map((q, i) => (
              <Reveal key={q.id} delay={i * 0.05}>
                <AccordionItem
                  value={q.id}
                  className="relative border border-white/10 bg-navy-surface data-[state=open]:border-gold/40 transition-colors overflow-hidden"
                >
                  {q.comingSoon && (
                    <div
                      className="pointer-events-none absolute right-[-56px] top-6 md:top-7 rotate-[24deg] bg-gold text-navy text-[10px] md:text-[11px] font-manrope tracking-[0.3em] uppercase px-16 py-1.5 shadow-lg z-10"
                      data-testid={`qualifi-coming-soon-${q.id}`}
                    >
                      Coming Soon
                    </div>
                  )}
                  <AccordionTrigger className="px-6 md:px-8 py-6 hover:no-underline group [&>svg]:hidden">
                    <div className="flex-1 text-left flex items-start md:items-center flex-col md:flex-row gap-3 md:gap-6">
                      <span className="inline-block text-[11px] tracking-[0.2em] uppercase text-navy bg-gold px-3 py-1.5">
                        {q.level}
                      </span>
                      <h3 className="font-playfair text-xl md:text-2xl text-white leading-snug">
                        {q.title}
                      </h3>
                    </div>
                    <ChevronDown size={22} strokeWidth={1.4} className="text-gold shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-10 pt-2">
                    <div className="hairline mb-8" />
                    <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 max-w-4xl">{q.overview}</p>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-10">
                      <Block icon={sectionIcons.learn} title="What You'll Learn">
                        <ul className="space-y-2.5">{q.learn.map((l, k) => <Bullet key={k}>{l}</Bullet>)}</ul>
                      </Block>
                      <Block icon={sectionIcons.entry} title="Entry Requirements">
                        <ul className="space-y-2.5">{q.entry.map((l, k) => <Bullet key={k}>{l}</Bullet>)}</ul>
                      </Block>
                      <Block icon={sectionIcons.structure} title="Qualification Structure">
                        <ul className="space-y-2.5">{q.structure.map((l, k) => <Bullet key={k}>{l}</Bullet>)}</ul>
                      </Block>
                      <Block icon={sectionIcons.assessment} title="Assessment">
                        <ul className="space-y-2.5">{q.assessment.map((l, k) => <Bullet key={k}>{l}</Bullet>)}</ul>
                      </Block>
                    </div>

                    <div className="mt-12">
                      <Block icon={sectionIcons.units} title="Course Units">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          {q.units.map((u, k) => (
                            <div key={k} className="border border-white/10 p-6 bg-navy">
                              <p className="text-[10px] tracking-[0.2em] uppercase text-gold mb-2">{u.code}</p>
                              <p className="font-playfair text-lg text-white leading-snug mb-3">{u.title}</p>
                              <p className="text-white/55 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                          ))}
                        </div>
                      </Block>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-10 mt-12">
                      <Block icon={sectionIcons.included} title="What's Included">
                        <ul className="space-y-2.5">{q.included.map((l, k) => <Bullet key={k}>{l}</Bullet>)}</ul>
                      </Block>
                      <Block icon={sectionIcons.progression} title="Career Progression">
                        <ul className="space-y-2.5">{q.progression.map((l, k) => <Bullet key={k}>{l}</Bullet>)}</ul>
                      </Block>
                    </div>

                    <div className="mt-12 border border-gold/25 bg-navy p-8">
                      <p className="text-[11px] tracking-[0.25em] uppercase text-gold mb-3">Why The Aesthetics Academy Co.</p>
                      <p className="text-white/75 leading-relaxed max-w-4xl">{q.why}</p>
                    </div>

                    {!q.comingSoon && (
                      <div className="mt-10 flex flex-wrap gap-4">
                        <GoldButton onClick={() => openEnquiry(q.title)} data-testid={`qualifi-enquire-${q.id}`}>
                          {ctaLabel}
                        </GoldButton>
                        <OutlineButton onClick={() => openEnquiry(q.title)} data-testid={`qualifi-more-${q.id}`}>
                          Request Full Details
                        </OutlineButton>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Reveal>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
